<template>
  <!-- ================================================================
       Dialog hiển thị lịch sử thanh toán của phiếu thu
       Sử dụng: <invoice-payment-history-dialog ref="paymentHistoryDialog" />
       Mở:      this.$refs.paymentHistoryDialog.open(row)
       ================================================================ -->
  <div>
    <dialog-base
      :visible.sync="visible"
      title="Lịch sử thanh toán phiếu thu"
      size="xl"
      :show-confirm="false"
      cancel-text="Đóng"
      cancel-type="danger"
      :use-panel="false"
      top="6vh"
      @cancel="handleClose"
    >
      <!-- Thông tin học sinh & Phiếu thu -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--success">
            <i class="el-icon-user" />
          </div>
          <div>
            <p class="sk-panel-title">{{ studentName || 'Đang tải...' }}</p>
            <p class="sk-panel-subtitle">
              Mã học sinh: <strong>{{ studentCode || '—' }}</strong> &mdash; 
              Mã phiếu thu: <strong>{{ invoiceCode || '—' }}</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Bảng danh sách lịch sử thanh toán -->
      <el-table
        v-loading="isLoading"
        :data="tableData"
        border
        style="width: 100%"
        class="sk-table"
        :empty-text="'Không có lịch sử thanh toán'"
      >
        <el-table-column type="index" label="STT" width="50" align="center" fixed />

        <el-table-column label="Ngày tạo" width="150" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.createdDate | formatDateTime }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Ngày thanh toán" width="120" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.datePayment | formatDate }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Người thao tác" width="150" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.nameAction || row.createdBy || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Người thanh toán" width="150" align="center">
          <template slot-scope="{ row }">
            <span>{{ row.parentPay || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Hình thức" width="150" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getPaymentMethodTagType(row.paymentMethod)" size="small" effect="plain">
              {{ paymentMethodLabel(row.paymentMethod) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Tiền mặt" align="right" width="120">
          <template slot-scope="{ row }">
            <span class="tdi-money">{{ row.cashMoney | formatCurrency }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Chuyển khoản" align="right" width="120">
          <template slot-scope="{ row }">
            <span class="tdi-money">{{ row.transferMoney | formatCurrency }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Đã thu" align="right" width="120">
          <template slot-scope="{ row }">
            <span class="tdi-money tdi-money--bold">{{ row.collectedMoney | formatCurrency }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Chuyển thanh toán" align="center" width="130">
          <template slot-scope="{ row }">
            <el-tag v-if="row.transactionEntry === 'MONEY_WALLET'" type="warning" size="mini" effect="light">
              Không chuyển
            </el-tag>
            <el-tag v-else-if="row.transactionEntry === 'MONEY_MONTH'" type="primary" size="mini" effect="light">
              Sang tháng sau
            </el-tag>
            <span v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column label="Mô tả" min-width="180" align="left">
          <template slot-scope="{ row }">
            <span class="description-text">{{ row.noteHistory || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Ảnh bill" width="100" align="center" prop="urlShow">
          <template slot-scope="{ row }">
            <div v-if="row.urlShow || row.urlPictureOrderSuccess">
              <el-image
                @click="showPictureHistory(row.urlShow || row.urlPictureOrderSuccess)"
                :src="row.urlShow || row.urlPictureOrderSuccess"
                style="width: 35px; height: 35px; border-radius: 4px; cursor: pointer;"
                class="el-image-opacity"
                fit="cover"
              />
            </div>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>
    </dialog-base>

    <!-- Dialog zoom ảnh bill -->
    <el-dialog
      :visible.sync="dialogVisibleImg"
      width="35%"
      append-to-body
      custom-class="sk-image-preview-dialog"
    >
      <div class="flex justify-center items-center p-2">
        <img
          :src="urlImgPicture"
          style="max-width: 100%; max-height: 70vh; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import ManagingStudentInvoiceService from "@/services/tutition_v3/ManagingStudentInvoiceService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "InvoicePaymentHistoryDialog",
  components: { DialogBase },
  data() {
    return {
      visible: false,
      isLoading: false,
      studentName: "",
      studentCode: "",
      invoiceCode: "",
      tableData: [],
      urlImgPicture: "",
      dialogVisibleImg: false,
    };
  },
  filters: {
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "—";
      const num = Number(value);
      if (isNaN(num)) return "—";
      const formattedValue = new Intl.NumberFormat("de", {
        style: "currency",
        currency: "VND",
      }).format(Math.abs(num));
      return num < 0 ? `(${formattedValue})` : formattedValue;
    },
  },
  methods: {
    // ─── Public API ────────────────────────────────────────────────
    /**
     * Mở dialog và load danh sách lịch sử thanh toán của phiếu thu
     * @param {Object} row - dữ liệu dòng học sinh/phiếu thu
     */
    open(row) {
      this.studentName = row.fullName || "";
      this.studentCode = row.studentCode || "";
      this.invoiceCode = row.invoiceCode || "";
      this.tableData = [];
      this.visible = true;
      this.fetchData(row.idx);
    },

    // ─── API Calls ─────────────────────────────────────────────────
    async fetchData(idx) {
      this.isLoading = true;
      try {
        const res = await ManagingStudentInvoiceService.getPaymentHistory(idx);
        const data = res?.data?.data || res?.data || [];
        this.tableData = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          collectedMoney: Number(item.collectedMoney || 0),
          cashMoney: Number(item.cashMoney || 0),
          transferMoney: Number(item.transferMoney || 0),
        }));
      } catch (err) {
        console.error(err);
        NotifyMessage.leftCornerNotificationError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Không thể tải lịch sử thanh toán của phiếu thu!"
        );
      } finally {
        this.isLoading = false;
      }
    },

    handleClose() {
      this.visible = false;
    },

    showPictureHistory(url) {
      this.urlImgPicture = url;
      this.dialogVisibleImg = true;
    },

    paymentMethodLabel(method) {
      const map = {
        TM: "Tiền mặt",
        CK: "Chuyển khoản",
        ONL: "Thanh toán online",
        budget: "Ngân sách",
        "TM/CK": "Tiền mặt & CK",
      };
      return map[method] || method || "—";
    },

    getPaymentMethodTagType(method) {
      const map = {
        TM: "info",
        CK: "",
        ONL: "success",
        budget: "warning",
        "TM/CK": "success",
      };
      return map[method] || "info";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";

/* ─── Money helper classes ─────────────────────────────────── */
.tdi-money {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-size: 13px;
}
.tdi-money--bold     { font-weight: 700; color: #1e293b; }

.description-text {
  word-break: break-word;
  white-space: pre-line;
  font-size: 13px;
}

.el-image-opacity:hover {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}
</style>
